export type FrontendLogLevel = "DEBUG" | "INFO" | "WARNING" | "ERROR" | "CRITICAL";

export type FrontendLogEvent = {
  ts?: string;
  level: FrontendLogLevel;
  service?: string;
  environment?: string;
  logger?: string;
  message: string;

  request_id?: string;
  trace_id?: string;
  span_id?: string;
  conversation_id?: string;
  user_id?: string;
  device_id?: string;
  tenant_id?: string;
  provider_id?: string;

  http_method?: string;
  http_path?: string;
  http_status?: number;
  duration_ms?: number;

  error_type?: string;
  error_message?: string;
  stacktrace?: string;
  fields?: Record<string, unknown>;
};

type LoggerOptions = {
  endpointUrl: string; // e.g. `${API_BASE_URL}/api/logs/events`
  getDefaultContext?: () => Partial<FrontendLogEvent>;
  maxBufferSize?: number;
  flushIntervalMs?: number;
};

const DEFAULT_MAX_BUFFER = 50;
const DEFAULT_FLUSH_INTERVAL_MS = 2000;

function nowIso(): string {
  return new Date().toISOString();
}

function safeString(value: unknown, maxLen: number): string {
  const str = typeof value === "string" ? value : JSON.stringify(value);
  return str.length > maxLen ? str.slice(0, maxLen) : str;
}

function redactFields(fields: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const deny = ["authorization", "cookie", "set-cookie", "password", "token", "secret"];
  for (const [k, v] of Object.entries(fields)) {
    if (deny.includes(k.toLowerCase())) continue;
    out[k] = v;
  }
  return out;
}

export function createFrontendLogger(options: LoggerOptions) {
  const buffer: FrontendLogEvent[] = [];
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let inFlight = false;

  const maxBufferSize = options.maxBufferSize ?? DEFAULT_MAX_BUFFER;
  const flushIntervalMs = options.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flush();
    }, flushIntervalMs);
  }

  function enqueue(event: FrontendLogEvent) {
    if (buffer.length >= maxBufferSize) {
      buffer.shift();
    }
    buffer.push(event);
    scheduleFlush();
  }

  async function flush() {
    if (inFlight) return;
    if (buffer.length === 0) return;

    inFlight = true;
    const batch = buffer.splice(0, buffer.length);
    try {
      await fetch(options.endpointUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ events: batch }),
      });
    } catch {
      // Drop on the floor: logging must never break UX.
    } finally {
      inFlight = false;
    }
  }

  function log(level: FrontendLogLevel, message: string, fields?: Record<string, unknown>) {
    const ctx = options.getDefaultContext?.() ?? {};
    enqueue({
      ts: nowIso(),
      level,
      service: ctx.service ?? "frontend",
      environment: ctx.environment,
      logger: ctx.logger ?? "frontend",
      message: safeString(message, 4000),
      request_id: ctx.request_id,
      trace_id: ctx.trace_id,
      span_id: ctx.span_id,
      conversation_id: ctx.conversation_id,
      user_id: ctx.user_id,
      device_id: ctx.device_id,
      tenant_id: ctx.tenant_id,
      provider_id: ctx.provider_id,
      fields: fields ? redactFields(fields) : undefined,
    });
  }

  function captureError(error: unknown, context?: Record<string, unknown>) {
    const err = error as any;
    const errorType = err?.name ? String(err.name) : "Error";
    const errorMessage = err?.message ? String(err.message) : safeString(error, 2000);
    const stacktrace = err?.stack ? String(err.stack) : undefined;

    const ctx = options.getDefaultContext?.() ?? {};
    enqueue({
      ts: nowIso(),
      level: "ERROR",
      service: ctx.service ?? "frontend",
      environment: ctx.environment,
      logger: ctx.logger ?? "frontend",
      message: safeString(errorMessage, 4000),
      request_id: ctx.request_id,
      trace_id: ctx.trace_id,
      span_id: ctx.span_id,
      conversation_id: ctx.conversation_id,
      user_id: ctx.user_id,
      device_id: ctx.device_id,
      tenant_id: ctx.tenant_id,
      provider_id: ctx.provider_id,
      error_type: errorType,
      error_message: safeString(errorMessage, 4000),
      stacktrace: stacktrace ? safeString(stacktrace, 16000) : undefined,
      fields: context ? redactFields(context) : undefined,
    });
  }

  return {
    log,
    captureError,
    flush,
  };
}
